import React from "react";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Input as AntInput } from "antd";
import { Form, Input } from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import bannerCashDrops from "./bannerCashDrops.svg";
import bannerFreeSpins from "./bannerFreeSpins.svg";
import bannerProfitBoost from "./bannerProfitBoost.png";
import type { BannerIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

export type BannerPreviewVariant = "cashDrops" | "freeSpins" | "profitBoost";

const BANNER_ART: Record<BannerPreviewVariant, string> = {
  cashDrops: bannerCashDrops,
  freeSpins: bannerFreeSpins,
  profitBoost: bannerProfitBoost,
};

type FieldName = (string | number)[] | string;

const TITLE_TEXT_SHADOW = "0 1px 4px var(--COLOR-BG_COLOR)";
const MESSAGE_TEXT_SHADOW =
  "0 1px 16px var(--COLOR-BG_COLOR), 0 1px 1px var(--COLOR-BG_COLOR)";
const DETAILS_TEXT_SHADOW = "0 1px 2px var(--COLOR-BG_COLOR)";
const TIMER_BOX_SHADOW =
  "0 1px 4px color-mix(in srgb, var(--COLOR-BG_COLOR) 50%, transparent)";
const CLAIM_BOX_SHADOW =
  "0 0 2px color-mix(in srgb, var(--COLOR-BG_COLOR) 50%, transparent)";

export type BannerFieldsProps = {
  /** Form.Item name for the banner title. */
  titleName: FieldName;
  /** Form.Item name for the banner message. */
  messageName: FieldName;
  titleLabel?: string;
  messageLabel?: string;
  titleRequired?: boolean;
  messageRequired?: boolean;
  titlePlaceholder?: string;
  messagePlaceholder?: string;
};

/** Editable banner title + message inputs (no preview). */
export const BannerFields: React.FC<BannerFieldsProps> = ({
  titleName,
  messageName,
  titleLabel = AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_LABEL,
  messageLabel = AppStrings.PAM_DYNAMIC_BONUS_BANNER_FIELDS_MESSAGE,
  titleRequired = false,
  messageRequired = false,
  titlePlaceholder = AppStrings.PAM_DYNAMIC_BONUS_BANNER_FIELDS_TITLE_PLACEHOLDER,
  messagePlaceholder = AppStrings.PAM_DYNAMIC_BONUS_BANNER_FIELDS_MESSAGE_PLACEHOLDER,
}) => (
  <>
    <Form.Item
      label={titleLabel}
      name={titleName}
      rules={
        titleRequired
          ? [
              {
                required: true,
                message: formatString(AppStrings.PAM_DYNAMIC_BONUS_FIELD_REQUIRED, {
                  title: titleLabel,
                }),
              },
            ]
          : []
      }
    >
      <Input placeholder={titlePlaceholder} />
    </Form.Item>
    <Form.Item
      label={messageLabel}
      name={messageName}
      rules={
        messageRequired
          ? [
              {
                required: true,
                message: formatString(AppStrings.PAM_DYNAMIC_BONUS_FIELD_REQUIRED, {
                  title: messageLabel,
                }),
              },
            ]
          : []
      }
    >
      <AntInput.TextArea rows={3} placeholder={messagePlaceholder} />
    </Form.Item>
  </>
);

export type BannerPreviewProps = {
  title?: string;
  message?: string;
  /** Decorative artwork shown on the right of the banner. */
  variant?: BannerPreviewVariant;
  backgroundColors?: string[];
  /** Border gradient stops; one color = solid, two+ = gradient. */
  borderColors?: string[];
  titleColor?: string;
  titleShadow?: boolean;
  messageColor?: string;
  messageShadow?: boolean;
  detailsColor?: string;
  detailsText?: string;
  detailsVisible?: boolean;
  detailsShadow?: boolean;
  claimText?: string;
  claimTextColor?: string;
  claimBackgroundColors?: string[];
  claimBorderColor?: string;
  claimBorderWidth?: number;
  claimShadow?: boolean;
  claimVisible?: boolean;
  timerBackgroundColor?: string;
  timerTextColor?: string;
  timerIconColor?: string;
  timerBorderColor?: string;
  timerBorderWidth?: number;
  timerShadow?: boolean;
  timerVisible?: boolean;
  imageUrl?: string;
};

const DEFAULT_BANNER_FILL =
  "linear-gradient(92deg, var(--COLOR-PRIMARY_BUTTON_0) 0.6%, var(--COLOR-PRIMARY_BUTTON_1) 97%)";

/**
 * Presentational promo preview mirroring the in-app banner (rounded box,
 * orange gradient, decorative art). Pass live values to update as the user
 * types; `variant` swaps the artwork to match the reward type.
 */
export const BannerPreview: React.FC<BannerPreviewProps> = ({
  title,
  message,
  variant = "cashDrops",
  backgroundColors,
  borderColors,
  titleColor,
  titleShadow,
  messageColor,
  messageShadow,
  detailsColor,
  detailsText,
  detailsVisible,
  detailsShadow,
  claimText,
  claimTextColor,
  claimBackgroundColors,
  claimBorderColor,
  claimBorderWidth,
  claimShadow,
  claimVisible,
  timerBackgroundColor,
  timerTextColor,
  timerIconColor,
  timerBorderColor,
  timerBorderWidth,
  timerShadow,
  timerVisible,
  imageUrl,
}) => {
  const resolvedTitle = (title ?? "").trim();
  const resolvedMessage = (message ?? "").trim();
  const titleClassName = resolvedTitle
    ? styles.bannerPreviewTitle
    : `${styles.bannerPreviewTitle} ${styles.bannerPreviewPlaceholder}`;
  const messageClassName = resolvedMessage
    ? styles.bannerPreviewMessage
    : `${styles.bannerPreviewMessage} ${styles.bannerPreviewPlaceholder}`;
  const gradientStops = (backgroundColors ?? []).filter(Boolean);
  const borderStops = (borderColors ?? []).filter(Boolean);
  const fillLayer = gradientStops.length
    ? `linear-gradient(92deg, ${gradientStops.join(", ")})`
    : DEFAULT_BANNER_FILL;

  const previewStyle: React.CSSProperties = {};
  if (borderStops.length > 0) {
    // Dual-layer background: fill clipped to padding-box, border gradient
    // clipped to border-box. Outline can't render multi-stop gradients.
    const borderLayer =
      borderStops.length === 1
        ? borderStops[0]
        : `linear-gradient(92deg, ${borderStops.join(", ")})`;
    previewStyle.background = `${fillLayer} padding-box, ${borderLayer} border-box`;
    previewStyle.border = "1px solid transparent";
    previewStyle.outline = "none";
  } else if (gradientStops.length > 0) {
    previewStyle.background = fillLayer;
  }
  const titleStyle: React.CSSProperties = {
    ...(titleColor ? { color: titleColor } : {}),
    ...(titleShadow ? { textShadow: TITLE_TEXT_SHADOW } : {}),
  };
  const messageStyle: React.CSSProperties = {
    ...(messageColor ? { color: messageColor } : {}),
    ...(messageShadow ? { textShadow: MESSAGE_TEXT_SHADOW } : {}),
  };
  const timerStyle: React.CSSProperties = {
    ...(timerBackgroundColor ? { background: timerBackgroundColor } : {}),
    ...(timerTextColor ? { color: timerTextColor } : {}),
    ...((timerBorderWidth ?? 0) > 0
      ? {
          borderColor: timerBorderColor || "var(--COLOR-PRIMARY)",
          borderStyle: "solid",
          borderWidth: `${timerBorderWidth}px`,
        }
      : {}),
    boxShadow: timerShadow === true ? TIMER_BOX_SHADOW : "none",
  };
  const claimGradient = (claimBackgroundColors ?? []).filter(Boolean);
  const claimStyle: React.CSSProperties = {
    ...(claimGradient.length
      ? { background: `linear-gradient(92deg, ${claimGradient.join(", ")})` }
      : {}),
    ...(claimTextColor ? { color: claimTextColor } : {}),
    ...(claimBorderColor || claimBorderWidth != null
      ? {
          borderColor: claimBorderColor || "var(--COLOR-PRIMARY)",
          borderStyle: "solid",
          borderWidth: `${claimBorderWidth ?? 1}px`,
        }
      : {}),
    boxShadow: claimShadow === true ? CLAIM_BOX_SHADOW : "none",
  };

  return (
    <div
      data-testid="banner-preview"
      className={styles.bannerPreview}
      style={previewStyle}
    >
      <div className={styles.bannerPreviewArtFrame}>
        <img
          data-testid="banner-preview-image"
          className={styles.bannerPreviewArt}
          src={imageUrl || BANNER_ART[variant]}
          alt=""
          aria-hidden="true"
        />
      </div>
      <div className={styles.bannerPreviewContent}>
        <div className={styles.bannerPreviewCopy}>
          <div className={styles.bannerPreviewTopRow}>
            <h3
              data-testid="banner-preview-title"
              className={titleClassName}
              style={titleStyle}
            >
              {resolvedTitle ||
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_PREVIEW_FALLBACK}
            </h3>
            <span className={styles.bannerPreviewTimerFrame}>
              {timerVisible && (
                <span className={styles.bannerPreviewTimer} style={timerStyle}>
                  <ClockCircleOutlined
                    className={styles.bannerPreviewTimerIcon}
                    style={
                      timerIconColor ? { color: timerIconColor } : undefined
                    }
                  />
                  {AppStrings.PAM_DYNAMIC_BONUS_BANNER_TIMER_PREVIEW}
                </span>
              )}
            </span>
          </div>
          <div className={styles.bannerPreviewMessageFrame}>
            <p
              data-testid="banner-preview-message"
              className={messageClassName}
              style={messageStyle}
            >
              {resolvedMessage ||
                AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW_MESSAGE_FALLBACK}
            </p>
          </div>
        </div>
        <div className={styles.bannerPreviewActions}>
          <span
            data-testid="banner-preview-details"
            className={styles.bannerPreviewDetails}
            style={{
              visibility: detailsVisible ? "visible" : "hidden",
              ...(detailsVisible && detailsColor
                ? { color: detailsColor }
                : {}),
              ...(detailsVisible && detailsShadow
                ? { textShadow: DETAILS_TEXT_SHADOW }
                : {}),
            }}
            aria-hidden={!detailsVisible}
          >
            {detailsText || AppStrings.DETAILS}
          </span>
          {claimVisible && (
            <span className={styles.bannerPreviewClaim} style={claimStyle}>
              {claimText || AppStrings.PAM_DYNAMIC_BONUS_BANNER_CLAIM_PLACEHOLDER}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const ConfiguredBannerPreview: React.FC<{ banner: BannerIn }> = ({
  banner,
}) => (
  <BannerPreview
    title={banner.title?.text?.en}
    message={banner.short_description?.text?.en}
    variant="profitBoost"
    backgroundColors={banner.background_color ?? undefined}
    borderColors={banner.border_color ?? undefined}
    titleColor={banner.title?.color ?? undefined}
    titleShadow={banner.title?.shadow}
    messageColor={banner.short_description?.color ?? undefined}
    messageShadow={banner.short_description?.shadow}
    detailsVisible={banner.details?.visible}
    detailsText={AppStrings.DETAILS}
    detailsColor={banner.details?.color ?? undefined}
    detailsShadow={banner.details?.shadow}
    claimVisible={banner.claim?.visible}
    claimText={banner.claim?.text?.en}
    claimTextColor={banner.claim?.text_color ?? undefined}
    claimBackgroundColors={banner.claim?.background_color}
    claimBorderColor={banner.claim?.border_color ?? undefined}
    claimBorderWidth={banner.claim?.border_width ?? undefined}
    claimShadow={banner.claim?.shadow}
    timerVisible={banner.timer?.visible}
    timerBackgroundColor={banner.timer?.background_color ?? undefined}
    timerTextColor={banner.timer?.text_color ?? undefined}
    timerIconColor={banner.timer?.icon_color ?? undefined}
    timerBorderColor={banner.timer?.border_color ?? undefined}
    timerBorderWidth={banner.timer?.border_width ?? undefined}
    timerShadow={banner.timer?.shadow}
    imageUrl={banner.image?.url ?? undefined}
  />
);

export type BannerDesignFieldProps = BannerFieldsProps & {
  /** Absolute path from the form root used to watch the title for the preview. */
  titleWatchName?: FieldName;
  /** Absolute path from the form root used to watch the message for the preview. */
  messageWatchName?: FieldName;
  /**
   * Whether to render the live promo preview alongside the inputs. Defaults
   * to true. Set to false for result types whose banner copy isn't an
   * in-app offer banner (e.g. AWARD results that reuse `banner_title_key`/
   * `banner_msg_key` for a push-notification title/message) — showing the
   * offer-style mockup there would be misleading.
   */
  showPreview?: boolean;
};

/**
 * Banner copy editor. By default renders a two-column layout: title +
 * message inputs on the left, and a live promo preview on the right that
 * re-renders as the admin types. With `showPreview={false}`, renders just
 * the inputs (single column, no mockup).
 */
const BannerDesignField: React.FC<BannerDesignFieldProps> = ({
  titleName,
  messageName,
  titleWatchName,
  messageWatchName,
  showPreview = true,
  ...fieldProps
}) => {
  const form = Form.useFormInstance();
  const watchedTitle = Form.useWatch(titleWatchName ?? titleName, form) as
    | string
    | undefined;
  const watchedMessage = Form.useWatch(messageWatchName ?? messageName, form) as
    | string
    | undefined;

  if (!showPreview) {
    return (
      <BannerFields titleName={titleName} messageName={messageName} {...fieldProps} />
    );
  }

  return (
    <div className={styles.bannerDesign}>
      <div className={styles.bannerDesignForm}>
        <BannerFields
          titleName={titleName}
          messageName={messageName}
          {...fieldProps}
        />
      </div>

      <div className={styles.bannerDesignPreview}>
        <p className={styles.bannerDesignPreviewLabel}>Preview</p>
        <BannerPreview title={watchedTitle} message={watchedMessage} />
      </div>
    </div>
  );
};

export default BannerDesignField;
